import type { TemplateCard as TemplateCardData, TemplateCategory, TemplateTag } from '@/lib/cms'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { useTranslation } from '@/i18n'
import { ScrollTrigger } from '@/lib/gsap'
import { EmptyState } from './empty-state'
import { FilterDropdown } from './filter-dropdown'
import { TemplateCard } from './template-card'

interface TemplatesSectionProps {
    templates: TemplateCardData[]
    categories: TemplateCategory[]
    tags: TemplateTag[]
    perPage?: number
}

export function TemplatesSection({ templates, categories, tags, perPage = 12 }: TemplatesSectionProps) {
    const router = useRouter()
    const { t } = useTranslation()

    const [category, setCategory] = useState<string | null>(null)
    const [tag, setTag] = useState<string | null>(null)
    const [shown, setShown] = useState(perPage)

    // Pick up filters coming from footer links (?category= / ?tag=).
    useEffect(() => {
        if (!router.isReady) return

        const q = router.query
        setCategory(typeof q.category === 'string' ? q.category : null)
        setTag(typeof q.tag === 'string' ? q.tag : null)
        setShown(perPage)
    }, [router.isReady, router.query])

    const tagNames = useMemo(() => {
        const map: Record<string, string> = {}
        tags.forEach((item) => (map[item.slug] = item.name))

        return map
    }, [tags])

    const filtered = useMemo(() => {
        return templates.filter((template) => {
            const matchesCategory = !category || template.template_category === category
            const matchesTag =
                !tag ||
                template.primary_tag === tag ||
                (template.tags?.includes(tag) ?? false)

            return matchesCategory && matchesTag
        })
    }, [templates, category, tag])

    const hasFilter = Boolean(category || tag)

    const clearFilters = () => {
        setCategory(null)
        setTag(null)
        setShown(perPage)
    }

    // When filtering collapses the grid, the page gets shorter and the footer scrolls
    // into view without a scroll event firing — so its GSAP ScrollTrigger reveal never
    // re-evaluates and stays hidden. Refresh triggers after the layout settles.
    useEffect(() => {
        const raf = requestAnimationFrame(() => ScrollTrigger.refresh())

        return () => cancelAnimationFrame(raf)
    }, [filtered.length, shown])

    // "Load more" keeps every card link in the server HTML (crawlable); cards past
    // the shown count are only visually hidden, then revealed in batches.
    const hasMore = shown < filtered.length

    return (
        <div className='template-collection-wrapper-outer'>
            {/* Inline zIndex: Webflow ships a `.relative { z-index: 1 }` utility that would
                otherwise override Tailwind's z-* classes and drop this below the cards. */}
            <div className='relative mb-10 flex flex-wrap items-center gap-3' style={{ zIndex: 30 }}>
                <FilterDropdown
                    label={t.home.categories}
                    options={categories}
                    activeSlug={category}
                    onSelect={(value) => {
                        setCategory(value)
                        setShown(perPage)
                    }}
                    allLabel={t.home.allCategories}
                    searchPlaceholder={t.home.searchCategories}
                    emptyLabel={t.home.noResults}
                />
                <FilterDropdown
                    label={t.home.tags}
                    options={tags}
                    activeSlug={tag}
                    onSelect={(value) => {
                        setTag(value)
                        setShown(perPage)
                    }}
                    allLabel={t.home.allTags}
                    searchPlaceholder={t.home.searchTags}
                    emptyLabel={t.home.noResults}
                />
            </div>

            <div className='template-collection-wrapper w-dyn-list relative z-0'>
                {filtered.length > 0 ? (
                    <div role='list' className='template-grid-new w-dyn-items'>
                        {filtered.map((template, index) => (
                            <TemplateCard
                                key={template.slug}
                                template={template}
                                primaryTagName={template.primary_tag ? tagNames[template.primary_tag] : undefined}
                                hidden={index >= shown}
                            />
                        ))}
                    </div>
                ) : hasFilter ? (
                    <EmptyState
                        title={t.home.emptyTitle}
                        description={t.home.emptyBody}
                        actionLabel={t.home.clearFilters}
                        onAction={clearFilters}
                    />
                ) : (
                    <EmptyState
                        title={t.home.emptyTitle}
                        description={t.home.emptyBody}
                        actionLabel={t.home.browseAll}
                        actionHref='/templates'
                    />
                )}

                {hasMore && (
                    <div className='load-more-wrapper'>
                        <button
                            type='button'
                            className='button button-white-rounded load-more-button w-button'
                            onClick={() => setShown((value) => value + perPage)}
                        >
                            {t.home.loadMore}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
